// 표준화된 PIE 차트 옵션 생성
import { formatValue } from '../../../utils/numberFormatter';
import { applyCommonStyles } from './applyCommonStyles';

export const generatePieChartOption = (data, baseOption, options = {}) => {
  // 표준 구조(문서 준수):
  // - 카테고리형: { categories: string[], series: [{ name, values: number[] }] }
  // - 시리즈형:   { series: [{ name: string, values: number|number[] }] }
  const categories = Array.isArray(data.categories) ? data.categories : null;
  const series = Array.isArray(data.series) ? data.series : [];

  // PIE 데이터 구성
  let pieData = [];
  if (categories && series.length > 0 && Array.isArray(series[0].values)) {
    // 카테고리형: 첫 번째 시리즈의 values를 카테고리와 매핑
    const values = series[0].values;
    const len = Math.min(categories.length, values.length);
    pieData = Array.from({ length: len }).map((_, i) => {
      const v = values[i];
      if (typeof v === 'object' && v !== null && v.value !== undefined) {
        return { name: categories[i] ?? '항목', value: v.value, __row: v.__row, __cols: v.__cols, __extras: v.__extras };
      }
      return { name: categories[i] ?? '항목', value: v ?? 0 };
    });
  } else if (series.length > 0) {
    // 시리즈형: 각 시리즈를 하나의 슬라이스로 매핑
    pieData = series.map(seriesItem => {
      const raw = Array.isArray(seriesItem.values) ? seriesItem.values[0] : seriesItem.values;
      if (typeof raw === 'object' && raw !== null && raw.value !== undefined) {
        return { name: seriesItem.name || '항목', value: raw.value, itemStyle: seriesItem.itemStyle, __row: raw.__row, __cols: raw.__cols, __extras: raw.__extras };
      }
      return { name: seriesItem.name || '항목', value: raw, itemStyle: seriesItem.itemStyle };
    });
  }

  // 최후 보정: raw_data 기반 (SELECT source, amount 형태 등)
  if ((!pieData || pieData.length === 0) && Array.isArray(data.raw_data)) {
    const rows = data.raw_data;
    pieData = rows.map((row) => {
      const keys = Object.keys(row);
      const nameKey = keys[0];
      const valueKey = keys[1];
      return {
        name: String(row[nameKey]),
        value: Number(row[valueKey])
      };
    });
  }

  // 차트 크기와 라벨 위치 정보 먼저 계산
  const containerWidth = options.__containerWidth || 500;
  const containerHeight = options.__containerHeight || 400;
  const containerSize = Math.min(containerWidth, containerHeight);
  const showDataLabels = options.advanced?.showDataLabels === true;
  const innerRadius = options.style?.innerRadius ?? 40; // 사용자 지정 내부 반지름 (기본값 40%)
  const labelPosition = options.advanced?.labelPosition || (innerRadius > 0 ? 'outside' : 'inside');
  
  // 범례 위치와 표시 여부에 따라 파이 차트 중심 조정
  const legendPosition = options.advanced?.legendPosition || 'left';
  const showLegend = options.advanced?.showLegend !== false;
  let pieCenter = ['50%', '50%'];
  
  // 범례가 표시될 때 파이 차트 중심점 조정하여 겹침 방지
  if (showLegend) {
    switch(legendPosition) {
      case 'left':
        pieCenter = ['60%', '50%']; // 오른쪽으로 이동
        break;
      case 'right':
        pieCenter = ['40%', '50%']; // 왼쪽으로 이동
        break;
      case 'top':
        pieCenter = ['50%', '60%']; // 아래로 이동
        break;
      case 'bottom':
        pieCenter = ['50%', '50%']; // 중앙 유지 (범례는 차트 영역 밖)
        break;
      default:
        pieCenter = ['50%', '50%'];
    }
  } 
  // 범례가 없을 때: 중앙 배치
  else {
    pieCenter = ['50%', '50%']; // 중앙 배치 (파이 크기로 라벨 공간 확보)
  }
  
  // 라벨 텍스트 최대 길이 계산 (실제 데이터 기반, 한글 및 폰트 사이즈 고려)
  let maxLabelLength = 0;
  let maxLabelPixelWidth = 0;
  
  // 라벨 폰트 사이즈 (스타일 옵션에서 가져오거나 기본값 12px)
  const labelFontSize = options.style?.fonts?.seriesLabel?.fontSize || 
                        options.style?.fonts?.value?.fontSize || 
                        12;
  
  if (showDataLabels && labelPosition === 'outside' && pieData.length > 0) {
    pieData.forEach(item => {
      // 예상 라벨 텍스트: "이름: 값 (퍼센트%)"
      const estimatedText = `${item.name || ''}: ${formatValue(item.value)} (100%)`;
      if (estimatedText.length > maxLabelLength) {
        maxLabelLength = estimatedText.length;
      }
      
      // 픽셀 너비 추정 (폰트 사이즈 고려, 보수적으로 계산)
      let charCount = { wide: 0, narrow: 0 };
      
      for (let char of estimatedText) {
        // 한글, 한자 등 넓은 문자
        if (char.match(/[\u3000-\u303F\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FFF\uAC00-\uD7AF]/)) {
          charCount.wide++;
        } else {
          charCount.narrow++;
        }
      }
      
      // 폰트 사이즈 기반 픽셀 너비 계산 (보수적)
      // 한글: fontSize * 1.1 (한글은 폰트 사이즈보다 약간 더 넓음)
      // 영문/숫자: fontSize * 0.6 (영문은 폰트 사이즈의 60% 정도)
      const pixelWidth = (charCount.wide * labelFontSize * 1.1) + 
                        (charCount.narrow * labelFontSize * 0.6);
      
      if (pixelWidth > maxLabelPixelWidth) {
        maxLabelPixelWidth = pixelWidth;
      }
    });
    
    // 안전 여백 추가 (작은 차트만 10% 추가, 큰 차트는 그대로)
    if (containerSize < 550) {
      maxLabelPixelWidth = maxLabelPixelWidth * 1.1;  // 작은 차트: 10% 여백
    }
    // 큰 차트(550px 이상)는 여백 추가 없음 (라벨이 2줄이므로 충분)
  }
  
  // outerRadius 동적으로 조절
  let outerRadius;
  
  if (showDataLabels && labelPosition === 'outside') {
    // 라벨이 외부에 있을 때: 차트 크기에 비례하여 동적으로 조정
    // 차트 크기별 기본 outerRadius 설정 (점진적으로 증가)
    let baseRadius;
    if (containerSize < 250) {
      baseRadius = 22;  // 매우 작은 차트
    } else if (containerSize < 350) {
      baseRadius = 25;  // 작은 차트
    } else if (containerSize < 550) {
      baseRadius = 50;  // 중간 차트 (350-550px) - 적절한 크기 (58 → 50)
    } else if (containerSize < 700) {
      baseRadius = 68;  // 중대형 차트 (550-700px)
    } else if (containerSize < 900) {
      baseRadius = 78;  // 큰 차트
    } else if (containerSize < 1200) {
      baseRadius = 83;  // 매우 큰 차트
    } else {
      baseRadius = 85;  // 초대형 차트
    }
    
    // 라벨 길이에 따라 파이 크기 축소 (작은 차트만 적용, 큰 차트는 거의 축소 안 함)
    if (containerSize < 550) {
      // 작은/중간 차트(< 550px): 라벨 길이에 따라 축소
      if (maxLabelPixelWidth > 250) {
        outerRadius = Math.max(28, baseRadius - 6);
      } else if (maxLabelPixelWidth > 200) {
        outerRadius = Math.max(30, baseRadius - 5);
      } else if (maxLabelPixelWidth > 150) {
        outerRadius = Math.max(32, baseRadius - 4);
      } else if (maxLabelPixelWidth > 120) {
        outerRadius = Math.max(35, baseRadius - 2);
      } else {
        outerRadius = baseRadius;  // 120 이하는 축소 안 함
      }
    } else {
      // 큰 차트(≥ 550px): 라벨 축소 거의 안 함 (라벨이 2줄이므로)
      if (maxLabelPixelWidth > 300) {
        outerRadius = Math.max(55, baseRadius - 3);  // 극단적으로 긴 라벨만 약간 축소
      } else {
        outerRadius = baseRadius;  // 기본값 그대로 사용
      }
    }
    
  } else {
    // 라벨이 내부에 있거나 표시 안할 때: 더 크게
    outerRadius = 75;
  }
  
  // 시리즈 설정
  const labelFormat = options.advanced?.labelFormat || '{name}: {value}{unit} ({percent}%)';
  const unitText = options.unit || '';

  const makePieLabelFormatter = () => (params) => {
    const formatted = formatValue(params.value);
    const percent = params.percent != null ? params.percent : '';
    
    // 기본 라벨 텍스트 생성
    let labelText = labelFormat
      .replace('{name}', params.name ?? '')
      .replace('{seriesName}', params.seriesName ?? '')
      .replace('{value}', formatted)
      .replace('{unit}', unitText)
      .replace('{percent}', String(percent));
    
    // 외부 라벨이고 텍스트가 긴 경우 자동 줄바꿈
    if (labelPosition === 'outside') {
      // 텍스트 길이 계산 (한글 고려)
      let textLength = 0;
      for (let char of labelText) {
        if (char.match(/[\u3000-\u303F\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FFF\uAC00-\uD7AF]/)) {
          textLength += 2; // 한글은 2칸
        } else {
          textLength += 1; // 영문/숫자는 1칸
        }
      }
      
      // 긴 라벨은 2줄로 분리 (적절한 위치에서 줄바꿈)
      // 기본 포맷: "이름: 값 (퍼센트%)"
      // → "이름:\n값 (퍼센트%)"
      if (textLength > 12) {  // 한글 기준 약 6자 이상이면 줄바꿈 (대부분의 라벨 포함)
        // ": " 뒤에서 줄바꿈 (이름과 값 분리)
        labelText = labelText.replace(': ', ':\n');
      }
    }
    
    return labelText;
  };

  baseOption.series = [{
    name: '데이터',
    type: 'pie',
    radius: [`${innerRadius}%`, `${outerRadius}%`], // 도넛 차트 형태
    center: pieCenter,
    avoidLabelOverlap: false,
    silent: false,  // 클릭 이벤트 활성화
    itemStyle: {
      borderRadius: 10,
      borderColor: '#fff',
      borderWidth: 2
    },
    label: {
      show: showDataLabels,
      position: labelPosition,  // 동적으로 계산된 labelPosition 사용
      fontSize: labelFontSize,  // 폰트 사이즈 명시
      formatter: makePieLabelFormatter(),
      // 외부 라벨일 때 lineHeight 설정 (2줄 라벨 지원)
      ...(labelPosition === 'outside' ? {
        lineHeight: labelFontSize + 2  // 줄 간격 (폰트 사이즈 + 2px)
      } : {})
    },
    emphasis: {
      label: {
        show: showDataLabels,
        fontSize: '20',
        fontWeight: 'bold'
      },
      itemStyle: {
        shadowBlur: 10,
        shadowOffsetX: 0,
        shadowColor: 'rgba(0, 0, 0, 0.5)'
      },
      scaleSize: 10  // 클릭/호버 시 확대 효과
    },
    selectedMode: 'single',  // 클릭 가능하도록 설정
    selectedOffset: 10,      // 선택된 조각이 바깥으로 나오는 거리
    labelLine: {
      show: showDataLabels && (labelPosition !== 'inside')
    },
    data: pieData
  }];

  // 범례 표시 여부 확인 (위에서 이미 선언된 변수 사용)
  if (showLegend || options.advanced?.legendPosition) {
    // 범례 설정 - legendPosition 옵션 처리 (위에서 이미 선언된 변수 사용)
    
    // 범례 순서 설정 (legendOrder가 있으면 해당 순서로, 없으면 pieData 순서대로)
    let legendData;
    if (options.advanced?.legendOrder && Array.isArray(options.advanced.legendOrder)) {
      // 사용자 지정 순서가 있는 경우
      const pieDataMap = new Map(pieData.map(item => [item.name, item]));
      legendData = options.advanced.legendOrder.filter(name => pieDataMap.has(name));
      // legendOrder에 없는 항목들은 뒤에 추가
      const remainingItems = pieData.filter(item => !options.advanced.legendOrder.includes(item.name));
      legendData = [...legendData, ...remainingItems.map(item => item.name)];
    } else {
      // 기본: pieData 순서대로
      legendData = pieData.map(item => item.name);
    }
    
    const legendConfig = {
      data: legendData,
      type: 'scroll'
    };
    
    // 위치별 설정 - 라인/바 차트와 동일한 방식으로 처리
    switch(legendPosition) {
      case 'top':
        legendConfig.top = 10;
        legendConfig.bottom = undefined;
        legendConfig.left = 'center';
        legendConfig.right = undefined;
        legendConfig.orient = 'horizontal';
        break;
      case 'bottom':
        legendConfig.top = undefined;
        legendConfig.bottom = 10;
        legendConfig.left = 'center';
        legendConfig.right = undefined;
        legendConfig.orient = 'horizontal';
        break;
      case 'left':
        legendConfig.top = 'center';
        legendConfig.bottom = undefined;
        legendConfig.left = 10;
        legendConfig.right = undefined;
        legendConfig.orient = 'vertical';
        break;
      case 'right':
        legendConfig.top = 'center';
        legendConfig.bottom = undefined;
        legendConfig.left = undefined;
        legendConfig.right = 10;
        legendConfig.orient = 'vertical';
        break;
      default:
        // 기본값: left
        legendConfig.top = 'center';
        legendConfig.bottom = undefined;
        legendConfig.left = 10;
        legendConfig.right = undefined;
        legendConfig.orient = 'vertical';
    }
    
    baseOption.legend = legendConfig;
  } else {
    // 범례 숨기기
    delete baseOption.legend;
  }

  // 색상 설정 (style.colors가 있으면 적용)
  if (options.style?.colors && Array.isArray(options.style.colors)) {
    baseOption.color = options.style.colors;
  }

  // 파이 차트는 그리드 불필요
  delete baseOption.grid;
  delete baseOption.xAxis;
  delete baseOption.yAxis;

  // 공통 스타일 적용
  if (options.style) {
    baseOption = applyCommonStyles(baseOption, options.style);
  }

  // transparent 처리 (ECharts 차트 배경 투명도)
  // showFrame은 DashboardComponentRenderer에서 처리하므로 여기서는 transparent만 처리
  if ((options.transparent === true || options.transparent === 'true')) {
    // ECharts 전체 배경을 투명하게 설정
    baseOption.backgroundColor = 'transparent';
  }

  return baseOption;
}; 